import moment from "moment";
import { BillingDetail } from "models/interfaces";
import myPlanTokens from "../MyPlan.tokens";
import { getCardCompany } from "../MyPlanFunctions";

export interface InvoiceRow {
  invoiceNumber: string;
  planName: string;
  startDate: string;
  endDate: string;
  paymentStatus: string;
  invoiceUrl: string;
  currency?: string;
  /** Invoice total before any account credit was spent. */
  gross: number;
  /** Credit consumed by this invoice. */
  creditApplied: number;
  /** Actually collected from the card — `gross` less the credit. */
  amountCharged: number;
  /** Seats billed on this invoice; `1` on a flat plan. */
  seats?: number;
  /**
   * RENEWAL · PLAN_CHANGE · SEAT_ADD · SEAT_REMOVE · SUBSCRIPTION_START ·
   * SMS_CREDIT_PURCHASE.
   */
  eventType?: string;
}

/** Human wording for the backend's invoice event types. */
export const invoiceEventLabel = (eventType?: string): string => {
  switch (eventType) {
    case "SUBSCRIPTION_START":
      return "Subscription started";
    case "PLAN_CHANGE":
      return "Plan change";
    case "SEAT_ADD":
      return "Seats added";
    case "SEAT_REMOVE":
      return "Seats removed";
    case "RENEWAL":
      return "Renewal";
    // A one-time SMS credit purchase, not a subscription charge — `planName`
    // already carries the quantity ("SMS Credits x 500").
    case "SMS_CREDIT_PURCHASE":
      return "SMS credits";
    default:
      return "";
  }
};

export interface PaymentCardRow {
  id: string;
  brand: string;
  number: string;
  isDefault: boolean;
  isExpired: boolean;
}

/** Plan & Billing renders the invoice list itself, so keep the rows as data. */
export const convertDataToTableFormat = (
  data: BillingDetail[],
): InvoiceRow[] => {
  return data.map((response: any) => ({
    invoiceNumber: response?.invoiceNumber,
    planName: response?.planName,
    startDate: response?.startDate,
    endDate: response?.endDate,
    paymentStatus: response?.paymentStatus,
    invoiceUrl: response?.invoiceUrl,
    currency: response?.currency,
    // Credit-aware amounts: what the invoice came to, what the account's
    // balance covered, and what the card was actually charged.
    gross: Number(response?.gross) || 0,
    creditApplied: Number(response?.creditApplied) || 0,
    amountCharged: Number(response?.amountCharged) || 0,
    seats: response?.seats,
    eventType: response?.eventType,
  }));
};

export const convertCardTableData = (data: any[]): PaymentCardRow[] => {
  return data.map((item: any) => ({
    id: item.id,
    brand: item.brand,
    number: item.number,
    isDefault: Boolean(item.default),
    isExpired: Boolean(item.isExpired),
  }));
};

/** Brand logo asset for a Stripe brand id; undefined for unmapped networks. */
export const getCardLogo = (brand?: string) =>
  getCardCompany((brand ?? "").toLowerCase());

/**
 * Text plate fallback (label + colour) used when a network has no logo asset.
 */
export const getCardPlate = (brand?: string) => {
  const key = (brand ?? "").toLowerCase();
  return {
    label: myPlanTokens.cardBrandLabel[key] ?? myPlanTokens.cardBrandLabel.fallback,
    color: myPlanTokens.cardBrand[key] ?? myPlanTokens.cardBrand.fallback,
  };
};

/** Design shows whole amounts without decimals and fractional ones with two. */
export const formatPlanAmount = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

/** Invoice period dates render as MM/DD/YYYY in the design. */
export const formatInvoiceDate = (value?: string) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
};

/** Status pill palette, keyed by the invoice payment status. */
export const getInvoiceStatusColors = (status?: string) => {
  const key = (status ?? "").toLowerCase();
  if (key === "paid" || key === "success" || key === "succeeded") {
    return {
      color: myPlanTokens.color.successText,
      bgcolor: myPlanTokens.color.successBg,
    };
  }
  if (key === "failed" || key === "unpaid" || key === "void") {
    return {
      color: myPlanTokens.color.dangerText,
      bgcolor: myPlanTokens.color.dangerBg,
    };
  }
  return {
    color: myPlanTokens.color.body,
    bgcolor: myPlanTokens.color.hoverFill,
  };
};
